import { defineStore } from 'pinia'

export type TPlaceOrderHandOverMethod = 'drop-point' | 'self-send'

export interface IPlaceOrderSampleTatAssignment {
  sampleID: string
  tatID: string
}

// The TAT page's own slider/override state — kept here (not just in the view)
// so a refresh or back-and-forth navigation restores what the customer picked
// instead of snapping back to the slowest option.
export interface IPlaceOrderTatSelection {
  selectedTatID: string
  overridesByGroupKey: Record<string, string>
}

interface IPlaceOrderState {
  orderID: string
  sampleTatAssignments: IPlaceOrderSampleTatAssignment[]
  tatSelection: IPlaceOrderTatSelection | null
  handOverMethod: TPlaceOrderHandOverMethod | null
  dropPointID: string | null
  date: string | null
}

// Mirrors milva-pro-v3's Sampler cart (`sampler-cart-details`): sessionStorage,
// so it survives a refresh but not a closed tab, and is cleared once the order
// is placed. Only ever holds the one draft order currently being placed.
const SESSION_KEY = 'lab-customer-place-order'

function getEmptyState(): IPlaceOrderState {
  return {
    orderID: '',
    sampleTatAssignments: [],
    tatSelection: null,
    handOverMethod: null,
    dropPointID: null,
    date: null,
  }
}

function loadPersistedState(): IPlaceOrderState {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return getEmptyState()
    const parsed = JSON.parse(raw) as Partial<IPlaceOrderState> | null
    if (typeof parsed !== 'object' || parsed === null) return getEmptyState()
    return { ...getEmptyState(), ...parsed }
  } catch {
    // Storage unavailable or the entry is corrupt — start fresh, nothing to lose.
    return getEmptyState()
  }
}

function persistState(state: IPlaceOrderState): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state))
  } catch {
    // Storage unavailable/full — the in-memory state still works for this page load.
  }
}

// Ephemeral, client-only state for the "Lab Customer — Place Order" flow
// (TAT selection -> hand-over method & day -> final submit). Nothing here is
// persisted to Firestore until the final placeOrder() call on orderStore —
// this store only carries the in-progress selection between the flow's two
// pages, scoped to whichever draft order is currently being placed, and
// mirrors it to sessionStorage so a refresh doesn't lose it.
export const usePlaceOrderStore = defineStore('placeOrder', {
  state: (): IPlaceOrderState => loadPersistedState(),

  actions: {
    // A selection made for a different order is stale — starting fresh avoids
    // carrying anything across two different draft orders if the customer
    // backs out and starts placing a different one.
    claimOrder(orderID: string): void {
      if (this.orderID === orderID) return
      this.$patch({ ...getEmptyState(), orderID })
    },

    setTatSelection(orderID: string, tatSelection: IPlaceOrderTatSelection): void {
      this.claimOrder(orderID)
      this.tatSelection = tatSelection
      persistState(this.$state)
    },

    setTatAssignments(orderID: string, sampleTatAssignments: IPlaceOrderSampleTatAssignment[]): void {
      this.claimOrder(orderID)
      this.sampleTatAssignments = sampleTatAssignments
      persistState(this.$state)
    },

    setHandOver(handOver: {
      method: TPlaceOrderHandOverMethod
      dropPointID: string | null
      date: string
    }): void {
      this.handOverMethod = handOver.method
      this.dropPointID = handOver.dropPointID
      this.date = handOver.date
      persistState(this.$state)
    },

    resetStore(): void {
      // $patch, not $reset(): $reset() re-runs state(), which would reload the
      // very entry being cleared from sessionStorage.
      this.$patch(getEmptyState())
      try {
        sessionStorage.removeItem(SESSION_KEY)
      } catch {
        // Nothing to clear if storage is unavailable.
      }
    },
  },

  getters: {
    hasTatAssignments: (state): boolean => state.sampleTatAssignments.length > 0,
  },
})
